import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import classNames from "classnames";
import { Entry } from "contentful";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { AbstractIntlMessages, useTranslations } from "next-intl";
import { CSSProperties, useMemo } from "react";

import IconHeartPath from "@/icons/icon-heart-path";
import CartNotificationProvider from "~/contexts/cart-notification";
import { useCountryDialog } from "~/contexts/country-dialog-context";
import WishlistProvider from "~/contexts/wishlist";
import CrossCountry from "~/types/cross-country";
import Editorials from "~/types/editorials";
import type { PaginationMeta } from "~/types/pagination";
import contentfulUtils from "~/utils/contentful-utils";

import GlobalCouponProvider from "../../contexts/globalcoupon";
import CountryDialog from "../common/country-dialog";
import Icon from "../common/icon";
import LoyaltyPush from "../common/loyalty-push";
import SeoHead from "../common/seo-head";
import Header from "./header";

const Footer = dynamic(() => import("./footer"));
const MiniFooter = dynamic(() => import("./mini-footer"));
const Prefooter = dynamic(() => import("./prefooter"));

type Props = {
  children: JSX.Element;
  navigation: Entry<unknown>;
  prefooter: Entry<unknown> | null;
  footer: Entry<unknown>;
  search: Editorials.SearchPage;
  page?: Editorials.BasicPageTemplate | null;
  navColor?: Editorials.navColor;
  inPpd?: boolean;
  mainStyle?: CSSProperties;
  mainClasses?: string;
  hidePrefooter?: boolean;
  miniFooter?: boolean;
  root?: CrossCountry.DetailedProduct;
  selected?: CrossCountry.DetailedProduct;
  productChildren?: CrossCountry.Product[];
  slugs?: Record<string, string> | null;
  messages?: AbstractIntlMessages;
  isHomepage?: boolean;
  disableSeo?: boolean;
  lastProductCategory?: string;
  canonicalUrl?: string;
  pagination?: PaginationMeta;
  hreflangs?: { locale: string; url: string }[];
  seoTitle?: string;
};

export default function LayoutPage(props: Props) {
  const router = useRouter();
  const t = useTranslations();
  const isProductPage = router.pathname.includes("/p/") && !!props.root;
  const { countries } = useCountryDialog();

  // `router.asPath` includes the query string
  // This results in the `hreflang` and `canonical` meta tags to have incorrect values.
  const seoPath = useMemo(() => {
    const path = router.asPath.split("?", 1)[0];

    // NOTE: `router.asPath` includes the current locale on the server but not on the client.
    // Since this memo is isomorphic, we remove it from the start in the server environment.
    // See: https://nextjs.org/docs/pages/api-reference/functions/use-router#router-object
    return path.startsWith(`/${router.locale}/`) ? path.replace(`/${router.locale}/`, `/`) : path;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, router.locale]);

  const canonicalUrl = useMemo(() => {
    // Use canonical from props if provided (e.g., from pagination)
    if (props.canonicalUrl) {
      return props.canonicalUrl;
    }

    const baseUrl = `https://www.kikocosmetics.com/${router.locale}`;
    // [TS]: Canonical page should be always the one we expect to be, despite I might open the PDP using any string (or none) before the ID
    if (isProductPage) {
      return `${baseUrl}/p/${props.selected?.slug}-${props.selected?.product_id}/`;
    }

    return `${baseUrl}${seoPath}`;
  }, [router.locale, seoPath, isProductPage, props.selected?.slug, props.selected?.product_id, props.canonicalUrl]);

  const liveEntryHeader = useContentfulLiveUpdates(props.navigation);
  const liveEntryPrefooter = useContentfulLiveUpdates(props.prefooter);
  const liveEntryFooter = useContentfulLiveUpdates(props.footer);

  const allUnifiedCommerceLocales = useMemo(
    () =>
      countries.continents
        .flatMap((c) => c.countries)
        .filter((c) => c.isUnifiedCommerce)
        .flatMap((c) => c.cultures.map((c) => c.locale)),
    [countries]
  );

  const hrefLangs = useMemo(() => {
    const baseUrl = `https://www.kikocosmetics.com`;

    if (props.hreflangs) {
      return props.hreflangs;
    }

    if (isProductPage || !props.slugs || Object.keys(props.slugs).length === 0) {
      return allUnifiedCommerceLocales.map((locale) => ({ locale, url: `${baseUrl}/${locale}${seoPath}` }));
    }

    const output: { locale: string; url: string }[] = [];

    for (let [contentfulLocale, slug] of Object.entries(props.slugs)) {
      const routerLocales = Object.entries(contentfulUtils.localesMap)
        .filter(([_, onContentful]) => onContentful === contentfulLocale)
        .filter(([onRouter]) => allUnifiedCommerceLocales.includes(onRouter))
        .map(([onRouter, _]) => onRouter);

      //SEO issues: if the slug returned by the editorial pages is "homepage", remove the slug
      slug = slug.replace(process.env.NEXT_PUBLIC_HOMEPAGE_SLUG! || "", "");

      for (const locale of routerLocales) {
        const url = new URL(`${locale}/${slug}/`.replace("//", "/"), baseUrl);
        output.push({ locale, url: url.href });
      }
    }
    return output;
  }, [allUnifiedCommerceLocales, isProductPage, props.hreflangs, props.slugs, seoPath]);

  return (
    <CartNotificationProvider>
      <GlobalCouponProvider>
        <WishlistProvider>
          <>
            {props.page?.seoMetadata && !props.disableSeo && (
              <SeoHead
                entry={props.page.seoMetadata}
                productDetail={props.root ?? undefined}
                selected={props.selected ?? undefined}
                titleSuffix={t("generic.pageTitleSuffix")}
                titleOverride={props.seoTitle}
                canonical={canonicalUrl}
                hreflangs={hrefLangs}
                lastProductCategory={props.lastProductCategory}
                pagination={props.pagination}
              />
            )}
            <div style={{ position: "absolute", opacity: 0 }} aria-hidden="true">
              <Icon icon={IconHeartPath} />
            </div>
            <Header
              entry={liveEntryHeader}
              navColor={props.page?.navColor ? props.page.navColor : props.navColor || "transparent"}
              searchSuggestions={props.search.quickSearchSuggestions?.fields?.suggestions}
              inPpd={props.inPpd}
              messages={props.messages}
              isHomepage={props.isHomepage}
            />
            <main
              className={classNames({ contentBelowHeader: props.page?.contentBelowHeader || false }, props.mainClasses)}
              style={props.mainStyle}
            >
              {props.children}
              {!props.hidePrefooter && liveEntryPrefooter ? <Prefooter entry={liveEntryPrefooter} /> : null}
            </main>
            <CountryDialog />
            <LoyaltyPush />
            {props.miniFooter ? <MiniFooter entry={liveEntryFooter} /> : <Footer entry={liveEntryFooter} />}
          </>
        </WishlistProvider>
      </GlobalCouponProvider>
    </CartNotificationProvider>
  );
}
